import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../api/axios';
import { getSiteAdminTemplateCategoriesRoute, makeFeatureTemplateRoute } from '../../api/routes';
import PopUpPanel from '../PopUpPanel/PopUpPanel';
import './CreateFeatureTemplatePopup.scss';

const TEMPLATE_CREATION_PROGRESS_STAGES = [
    { minElapsedMs: 0, progress: 10, label: 'Preparing template…' },
    { minElapsedMs: 900, progress: 28, label: 'Copying feature data…' },
    { minElapsedMs: 2500, progress: 48, label: 'Resolving linked assets…' },
    { minElapsedMs: 5000, progress: 68, label: 'Building template campaign…' },
    { minElapsedMs: 9000, progress: 84, label: 'Finalising template…' },
    { minElapsedMs: 15000, progress: 92, label: 'Still working. Larger features can take a little while…' },
];

const CreateFeatureTemplatePopup = ({
    campaignId,
    campaignFeatureId,
    featureLabel,
    beforeCreate,
    onClose,
    onCreated,
}) => {
    const [name, setName] = useState(featureLabel ? `${featureLabel} Template` : '');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saveProgress, setSaveProgress] = useState(0);
    const [saveStatus, setSaveStatus] = useState('');

    useEffect(() => {
        let isMounted = true;

        axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true })
            .then((res) => {
                if (!isMounted) return;

                const nextCategories = res.data?.categories || [];
                setCategories(nextCategories);
                if (nextCategories.length === 1) {
                    setCategoryId(nextCategories[0].id || nextCategories[0]._id || '');
                }
            })
            .catch(() => {
                if (!isMounted) return;
                setError('Failed to load template categories.');
            })
            .finally(() => {
                if (isMounted) {
                    setLoadingCategories(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!saving) {
            setSaveProgress(0);
            setSaveStatus('');
            return undefined;
        }

        const startTime = Date.now();

        const updateProgress = () => {
            const elapsedMs = Date.now() - startTime;
            const activeStage = TEMPLATE_CREATION_PROGRESS_STAGES.reduce((selectedStage, stage) => (
                elapsedMs >= stage.minElapsedMs ? stage : selectedStage
            ), TEMPLATE_CREATION_PROGRESS_STAGES[0]);

            const nextProgress = Math.max(activeStage.progress, Math.min(94, Math.round(10 + (1 - Math.exp(-elapsedMs / 6000)) * 84)));
            setSaveProgress(nextProgress);
            setSaveStatus(activeStage.label);
        };

        updateProgress();
        const intervalId = window.setInterval(updateProgress, 250);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [saving]);

    const categoryOptions = useMemo(() => {
        const activeCategories = categories.filter((category) => category?.isActive === true);
        const inactiveCategories = categories.filter((category) => category?.isActive !== true);

        return [
            ...activeCategories,
            ...inactiveCategories,
        ].map((category) => ({
            id: category.id || category._id,
            name: category.isActive === true ? category.name : `${category.name} (inactive)`,
        }));
    }, [categories]);

    const handleCreate = async () => {
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();

        if (!trimmedName) {
            setError('Template name is required.');
            return;
        }

        if (!categoryId) {
            setError('Template category is required.');
            return;
        }

        setSaving(true);
        setError('');

        try {
            if (typeof beforeCreate === 'function') {
                await beforeCreate();
            }

            const response = await axios.post(makeFeatureTemplateRoute, {
                campaignID: campaignId,
                campaignFeatureId,
                name: trimmedName,
                categoryId,
                description: trimmedDescription,
            }, { withCredentials: true });

            if (response.data?.success && response.data?.templateId) {
                onCreated?.(response.data.templateId);
                return;
            }

            setError(response.data?.message || 'Failed to create feature template.');
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to create feature template.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <PopUpPanel
            showCloseBtn={!saving}
            closePanelFunc={onClose}
            className="create-feature-template-popup"
        >
            <div className="cftp-header">
                <h2 className="cftp-title">Create Feature Template</h2>
                <p className="cftp-subtitle">
                    Save <strong>{featureLabel || 'this feature'}</strong> as a reusable template.
                </p>
            </div>

            <div className="cftp-form">
                <label className="cftp-field">
                    <span className="cftp-label">Template name</span>
                    <input
                        className="cftp-input"
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Enter a template name"
                        disabled={saving}
                    />
                </label>

                <label className="cftp-field">
                    <span className="cftp-label">Category</span>
                    <select
                        className="cftp-select"
                        value={categoryId}
                        onChange={(event) => setCategoryId(event.target.value)}
                        disabled={loadingCategories || saving}
                    >
                        <option value="">Select a category</option>
                        {categoryOptions.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="cftp-field">
                    <span className="cftp-label">Description</span>
                    <textarea
                        className="cftp-textarea"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        placeholder="Optional description"
                        rows={4}
                        disabled={saving}
                    />
                </label>
            </div>

            {loadingCategories && <div className="cftp-status">Loading categories…</div>}
            {!loadingCategories && categoryOptions.length === 0 && (
                <div className="cftp-status cftp-status--warning">No template categories are available.</div>
            )}
            {saving && (
                <div className="cftp-progress" aria-live="polite">
                    <div className="cftp-progress__header">
                        <span className="cftp-progress__label">Creating template</span>
                        <span className="cftp-progress__value">{saveProgress}%</span>
                    </div>
                    <div className="cftp-progress__track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={saveProgress}>
                        <div className="cftp-progress__fill" style={{ width: `${saveProgress}%` }} />
                    </div>
                    <div className="cftp-progress__status">{saveStatus || 'Working…'}</div>
                </div>
            )}
            {error && <div className="cftp-error">{error}</div>}

            <div className="cftp-actions">
                <button
                    type="button"
                    className="cftp-button cftp-button--secondary"
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="cftp-button cftp-button--primary"
                    onClick={handleCreate}
                    disabled={saving || loadingCategories || categoryOptions.length === 0}
                >
                    {saving ? 'Creating…' : 'Create Template'}
                </button>
            </div>
        </PopUpPanel>
    );
};

export default CreateFeatureTemplatePopup;